import { gql } from '@apollo/client';
import { chakra, GridItem, useToast, VStack } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { ContentModalSubHeading } from '~components/displays/ContentModalSubHeading';
import { Link } from '~components/ui/Link';
import { ConfigContentSourceAppInputs } from '~graphql/__generated__/types';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import { ContentAppEntry } from '../ContentAppEntry';
import {
  ConfigApps_ConfigFragment,
  useConfigUpdateContentSourceAppsMutation,
} from './__generated__/ConfigApps.graphql';

export type ConfigApp = NonNullable<
  ConfigApps_ConfigFragment['configApps']
>['assignedApps'][number];

export function ConfigApps({
  configuration,
  configStatus,
}: {
  configuration: ConfigApps_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const isDisplaySettingsUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);
  const { organization } = useAuth();

  let organizationLink = '#';
  if (organization) {
    organizationLink = `/${organization.handle}/settings/apps`;
  }

  const assignedApps = configuration.configApps?.assignedApps ?? [];
  const unassignedApps = configuration.configApps?.unassignedApps ?? [];

  const availableApps: ConfigApp[] = [];

  unassignedApps.forEach((app) => {
    if (app.appVersions && app.appVersions.length > 0) {
      // Add to available list
      availableApps.push(app);
    }
  });
  const [updateContentSourceApps] = useConfigUpdateContentSourceAppsMutation();

  const mapToContentSourceAppInput = (
    subscriptions: ConfigApp[],
  ): ConfigContentSourceAppInputs[] => {
    return subscriptions.map((subscription) => ({
      applicationId: subscription.id,
      label: subscription.name,
      isBootable: subscription.isBootableValue,
    }));
  };
  const handleUpdateContentSourceApps = async (
    appSubscription: ConfigApp,
    action: 'add' | 'remove',
  ) => {
    try {
      let updatedApps: ConfigApp[];

      if (action === 'add') {
        updatedApps = [...assignedApps, appSubscription];
      } else if (action === 'remove') {
        updatedApps = assignedApps.filter((app) => app.id !== appSubscription.id);
      } else {
        updatedApps = assignedApps;
      }

      const mappedApps = mapToContentSourceAppInput(updatedApps);

      await updateContentSourceApps({
        variables: {
          input: {
            configurationId: configuration.id,
            configContentSourceApps: mappedApps,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorUpdatingApps'),
      });
    }
  };

  return (
    <GridItem colSpan={{ base: 8, md: 5 }}>
      <VStack spacing="3" alignItems="flex-start">
        <>
          {assignedApps && (
            <>
              <ContentModalSubHeading>{t('assigned')}</ContentModalSubHeading>
              {assignedApps.length > 0 ? (
                assignedApps.map((appSubscription) => {
                  return (
                    <ContentAppEntry
                      key={appSubscription.id}
                      subscription={appSubscription}
                      onDelete={() => handleUpdateContentSourceApps(appSubscription, 'remove')}
                      disabled={isDisplaySettingsUpdateDisabled}
                    />
                  );
                })
              ) : (
                <chakra.p>{t('appConfigInstallAlert')}.</chakra.p>
              )}
            </>
          )}
        </>

        <>
          {availableApps && (
            <>
              <ContentModalSubHeading>{t('available')}</ContentModalSubHeading>
              {availableApps.length > 0 ? (
                availableApps.map((appSubscription) => {
                  return (
                    <ContentAppEntry
                      key={appSubscription.id}
                      subscription={appSubscription}
                      onInstall={() => handleUpdateContentSourceApps(appSubscription, 'add')}
                      disabled={isDisplaySettingsUpdateDisabled}
                    />
                  );
                })
              ) : (
                <chakra.p>
                  {t('buyAppAlert')}{' '}
                  <Link to={organizationLink} color="blue.500">
                    {t('organisationApps')}
                  </Link>{' '}
                  {t('buyAppAlert1')}
                </chakra.p>
              )}
            </>
          )}
        </>
      </VStack>
    </GridItem>
  );
}

ConfigApps.graphql = {
  fragments: {
    ConfigurationApps: gql`
      fragment ConfigurationApps on ConfigurationApps {
        id
        name
        iconUrl
        appVersions
        isBootableValue
      }
    `,
    ConfigApps_config: gql`
      fragment ConfigApps_config on ConfigurationModel {
        id
        configApps {
          assignedApps {
            ...ConfigurationApps
          }
          unassignedApps {
            ...ConfigurationApps
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateContentSourceApps: gql`
      mutation ConfigUpdateContentSourceApps($input: ConfigurationUpdateContentSourceAppsInput!) {
        configurationUpdateContentSourceApps(input: $input) {
          id
        }
      }
    `,
  },
};
